import React from 'react';
import { Sparkles, MessageSquare } from 'lucide-react';
import type { FlowModel, FlowAnswers, Ratio, Style, Tone } from '../../../types';

interface GenerateTriggerPanelProps {
  flowModel: FlowModel;
  flowAnswers: FlowAnswers;
  ratios: Ratio[];
  styles: Style[];
  tones: Tone[];
  isGenerating: boolean;
  onBack: () => void;
  onGenerate: () => void;
}

const GenerateTriggerPanel: React.FC<GenerateTriggerPanelProps> = ({
  flowModel,
  flowAnswers,
  ratios,
  styles,
  tones,
  isGenerating,
  onBack,
  onGenerate,
}) => {
  return (
    <div className="space-y-6 md:space-y-8 w-full animate-in fade-in zoom-in duration-300">
      {/* 答案摘要 */}
      <div className="bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl p-4 flex flex-wrap gap-4 text-sm max-w-4xl mx-auto">
        {flowModel.questions
          .filter(q => q.type !== 'image')
          .map(q => {
            const val = flowAnswers[q.id];
            if (val === undefined || val === null || val === '') return null;

            let label: string | null = null;
            if (q.type === 'boolean') {
              if (!val) return null;
              label = '是';
            } else if (q.type === 'select') {
              const strVal = val as string;
              if (!strVal) return null;
              if (q.id === 'imageAspectRatio') label = ratios.find(r => r.id === strVal)?.name || strVal;
              else if (q.id === 'imageStyle') label = styles.find(s => s.id === strVal)?.name || strVal;
              else if (q.id === 'textToneInImage') label = tones.find(t => t.id === strVal)?.name || strVal;
              else label = q.options?.find(o => o.value === strVal)?.label || strVal;
            } else if (q.type === 'text') {
              const strVal = val as string;
              if (!strVal) return null;
              label = strVal;
            }

            if (!label) return null;

            const isLong = q.type === 'text' && label.length > 20;
            return (
              <div key={q.id} className={`flex items-center gap-2 ${isLong ? 'w-full' : ''}`}>
                <span className="text-slate-500 whitespace-nowrap">{q.title}：</span>
                <span className={`font-medium px-2 py-1 rounded-md ${isLong
                  ? 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 text-xs truncate flex-1'
                  : 'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200'
                }`}>
                  {isLong ? label.slice(0, 40) + '…' : label}
                </span>
              </div>
            );
          })
        }
      </div>

      {/* 生成按鈕區 */}
      <div className="min-h-[200px] flex flex-col items-center justify-center space-y-6 md:space-y-8">
        <div className="text-center space-y-3 md:space-y-4 max-w-lg">
          <div className="inline-block p-3 md:p-4 bg-indigo-500/10 rounded-2xl mb-2 md:mb-4">
            <MessageSquare size={40} className="text-indigo-500 dark:text-indigo-400 md:hidden" />
            <MessageSquare size={48} className="text-indigo-500 dark:text-indigo-400 hidden md:block" />
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">AI 對話生成</h3>
          <p className="text-sm md:text-base text-slate-500 dark:text-slate-400 leading-relaxed">
            先生成初始圖片，再透過 AI 對話逐步調整，精準打造最佳成果。
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto px-4 sm:px-0">
          <button
            onClick={onBack}
            className="px-6 py-3 md:py-4 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-2xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
          >
            返回上一步
          </button>
          <button
            onClick={onGenerate}
            disabled={isGenerating}
            className={`px-6 md:px-12 py-3 md:py-4 rounded-2xl font-bold flex items-center justify-center gap-3 shadow-xl transition-all ${isGenerating
              ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-500 hover:scale-105 active:scale-95'
            }`}
          >
            {isGenerating ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                生成中...
              </>
            ) : (
              <>
                開始生成 <Sparkles size={20} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateTriggerPanel;
